export interface StorageGetUploadUrlParams {
  path: string;
  contentType: string;
}
export interface StorageGetUploadUrlResult {
  signedUrl: string;
}

/** The status `storageGetUploadUrl` refuses with when the object is already
 * there and the caller cannot prove it owns it (#5016).
 *
 * It is a STATUS CODE and not a message because a message cannot reach the
 * client: `callApiAsync` throws `CloudFunctionHttpError(name, response.status)`
 * without reading the response body, so a declared function's `clientMessage`
 * never crosses the wire. `SERVER_BEHIND_ACCOUNT_VERSION_HTTP_STATUS` is the
 * same pattern.
 *
 * It has to be distinguishable from the plain refusal because the offline
 * outbox acts on it: an upload whose PUT reached the bucket while its response
 * was lost stays queued, and its retry lands here. Told apart, that retry can
 * be settled against the object that is already there; lumped in with the other
 * refusals it burns the item's attempts and is eventually reported as a photo
 * lost, which it is not. */
export const STORAGE_WRITE_OBJECT_EXISTS_HTTP_STATUS = 409;

export interface StorageGetDownloadUrlParams {
  path: string;
}
export interface StorageGetDownloadUrlResult {
  downloadUrl: string;
}

export interface StorageDeleteParams {
  path: string;
}

/** Why a delete did nothing. Only ever set when `deleted` is false.
 *
 * `not-owned` covers both a path belonging to somebody else and a path from
 * before owner tokens existed, because the caller can act on neither and
 * telling them apart would say something about another user's data.
 * `bad-path` is a shape no build of this app produces. `already-gone` is an
 * ordinary outcome — a retry, or two size variants racing. */
export type StorageDeleteSkipReason = 'not-owned' | 'bad-path' | 'already-gone';

export interface StorageDeleteResult {
  /** the call itself succeeded. Stays true for a refused delete: the caller
   * has nothing to retry and nothing to report, which is what keeps a
   * legitimate remove of somebody else's shared image quiet. */
  ok: boolean;
  /** whether an object was actually removed. Absent on an older server. */
  deleted?: boolean;
  reason?: StorageDeleteSkipReason;
}
